import { Filter } from "@/shared/lib/filter/filter";
import { backendApi } from "@/services/backend/api";
import { updateCache } from "@/services/backend/_lib/update-cache";
import {
  ReferenceNumberFilters,
  ResetReferenceNumberRequest,
} from "@/services/backend/documents/types/reference-number";
import { Address } from "@/services/backend/addresses/types/address";
import { Contact } from "@/services/backend/contacts/types/contact";
import { Representative } from "@/services/backend/representatives/types/representative";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { Lst } from "@/shared/lib/lst/lst";
import { providesList } from "@/services/backend/_lib/tag-helpers";
import { Order } from "@/services/backend/rlt/orders/types/order";
import { DocumentState } from "@/services/backend/documents/types/state";

const orderApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    rltOrderPut: build.mutation<Order, PutRequest>({
      query: (request) => ({
        url: `/rlt/order`,
        method: "PUT",
        body: request,
      }),
      invalidatesTags: (result, _e, request) => [
        { type: "RltOrder", id: request.id },
        { type: "RltOrder", id: "LIST" },
        { type: "RltTreatment", id: result?.treatmentId },
        { type: "RltCalculation", id: result?.calculationId },
      ],
    }),
    rltOrderShow: build.query<Order, ShowRequest>({
      query: (request) => ({
        url: `/rlt/order`,
        method: "GET",
        params: request,
      }),
      providesTags: (result) =>
        result ? [{ type: "RltOrder", id: result.id }] : [],
    }),
    rltOrderPatch: build.mutation<Order, PatchRequest>({
      query: (request) => ({
        url: `/rlt/order`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltOrder", id: request.id },
        { type: "RltOrder", id: "LIST" },
      ],
    }),
    rltOrderResetReferenceNumber: build.mutation<
      Order,
      ResetReferenceNumberRequest
    >({
      query: (request) => ({
        url: `/rlt/order/reset-reference-number`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltOrder", id: request.id },
        { type: "RltOrder", id: "LIST" },
      ],
    }),
    rltOrderList: build.query<Lst<Order>, Partial<ListRequest>>({
      query: (request) => ({
        url: `/rlt/order/list`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) => providesList(result?.items, "RltOrder"),
    }),
    rltOrderUpdatePostalAddress: build.mutation<
      Order,
      UpdatePostalAddressRequest
    >({
      query: (request) => ({
        url: `/rlt/order/postal-address`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltOrder", id: request.id },
        "RltOrder",
      ],
    }),
    rltOrderUpdateServiceAddress: build.mutation<
      Order,
      UpdateServiceAddressRequest
    >({
      query: (request) => ({
        url: `/rlt/order/service-address`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltOrder", id: request.id },
        "RltOrder",
      ],
    }),
    rltOrderUpdateServiceContact: build.mutation<
      Order,
      UpdateServiceContactRequest
    >({
      query: (request) => ({
        url: `/rlt/order/service-contact`,
        method: "PATCH",
        body: {
          id: request.id,
          serviceContact: request.contact,
        },
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltOrder", id: request.id },
        "RltOrder",
      ],
    }),
    rltOrderUpdateRepresentative: build.mutation<
      Order,
      UpdateRepresentativeRequest
    >({
      query: (request) => ({
        url: `/rlt/order/representative`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltOrder", id: request.id },
        "RltOrder",
      ],
    }),
    rltOrderUpdateState: build.mutation<Order, UpdateStateRequest>({
      query: (request) => ({
        url: `/rlt/order/state`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltOrder", id: request.id },
        "RltOrder",
      ],
    }),
    rltOrderDelete: build.mutation({
      query: (request) => ({
        url: `rlt/order`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltOrder", id: "LIST" },
        { type: "RltOrder", id: request.id },
      ],
    }),
  }),
});

export const {
  useRltOrderPutMutation,
  useRltOrderShowQuery,
  useRltOrderPatchMutation,
  useRltOrderResetReferenceNumberMutation,
  useRltOrderListQuery,
  useRltOrderUpdatePostalAddressMutation,
  useRltOrderUpdateServiceAddressMutation,
  useRltOrderUpdateServiceContactMutation,
  useRltOrderUpdateRepresentativeMutation,
  useRltOrderUpdateStateMutation,
  useRltOrderDeleteMutation,
} = orderApi;

// PatchRequest updates the document. All fields except `id` are optional:
// an omitted key leaves the corresponding value unchanged.
export interface PatchRequest {
  id: string;
  referenceNumber?: string;
}

export interface PutRequest {
  id: string;
  processId: string | null;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
  offerId: string | null;
  date: string | null;
  channel: string | null;
  customerReference: string | null;
  internalComment: string | null;
}

export interface ShowRequest {
  id: string;
}

export interface UpdateStateRequest {
  id: string;
  state: DocumentState;
  stateSetBy: string;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters, ReferenceNumberFilters {
  customerReference?: Filter<string>;
  orderBy?: OrdrBy<string>[];
  date?: Filter<string>;
  state?: Filter<DocumentState>;
  limit?: number;
  offset?: number;
}

export interface UpdatePostalAddressRequest {
  id: string;
  postalAddress: Address;
}

export interface UpdateServiceAddressRequest {
  id: string;
  serviceAddress: Address;
}

export interface UpdateServiceContactRequest {
  id: string;
  contact: Contact;
}

export interface UpdateRepresentativeRequest {
  id: string;
  representative: Representative;
}

export const updateRltOrderCache = updateCache(orderApi, "rltOrderShow");
